import React from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { match } from 'react-router-dom'

import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import {
  Item,
  itemsActions,
  Feature,
  Taste,
  ItemImageType,
  ItemImage,
} from '../../core/items'
import {
  updateItemTranslation,
  updateTasteTranslation,
  updateFeatureTranslation,
} from '../../core/i18n'
import { LANGUAGE_CODES, SupportedLanguageCode } from '../../core/i18n'
import { Permission } from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import ManufacturingInfoForm from '../components/each-item/manufacturing-info-form'
import ItemInfoTable from '../components/each-item/item-info-table'
import TastesTable from '../components/each-item/tastes-table'
import ItemImageItems from '../components/each-item/item-image-items'
import { strings } from '../components/each-item/each-item-content'
import styles from '../styles/modules/each-item/each-item.module.scss'
import ManufacturingInfoTable from '../components/each-item/manufacturing-info-table'
import ItemInfoForm from '../components/each-item/item-info-form'
import FeaturesForm from '../components/each-item/features-form'
import TastesForm from '../components/each-item/tastes-form'
import Button from '../components/button'
import ItemImageForms from '../components/each-item/item-image-forms'
import MatchItemInfoTable from '../components/each-item/match-item-info-table'
import MatchItemInfoForm from '../components/each-item/match-item-info-form'
import FeaturesTable from '../components/each-item/features-table'
import PageLanguageTabs from '../components/common/page-language-tabs'

interface Props {
  match: match<any>
  currentItem: Item | null
  itemImage: ItemImage
  fetchItem: (id: number) => void
  setItem: (id: number, item: Item) => void
  uploadItemImage: (
    id: number,
    itemImageType: ItemImageType,
    file: File
  ) => void
}

interface State {
  readonly isEditing: boolean
  readonly itemEditing: Item
  readonly newFeature: Feature
  readonly newTaste: Taste
  readonly pageActiveLanguage: SupportedLanguageCode
}

class EachItemScreen extends React.Component<Props, State> {
  // 定数定義
  private static readonly NUMERIC_PATTERN = /^[0-9\b]+$/
  private static readonly TRANSLATION_PREFIX = 'itemTasteTranslations.'
  private static readonly FEATURE_TRANSLATION_PREFIX =
    'itemFeatureTranslations.'

  constructor(props: Props) {
    super(props)
    const id = this.props.match.params.id
    this.props.fetchItem(Number(id))
    this.state = {
      isEditing: false,
      itemEditing: null,
      newFeature: {
        name: '',
        content: '',
        itemFeatureTranslations: [
          { langCode: LANGUAGE_CODES.ENGLISH, name: '', content: '' },
        ],
      },
      newTaste: {
        value: null,
        name: '',
        itemTasteTranslations: [{ langCode: LANGUAGE_CODES.ENGLISH, name: '' }],
      },
      pageActiveLanguage: LANGUAGE_CODES.JAPANESE,
    }
  }

  componentDidMount = () => {
    const id = this.props.match.params.id
    this.props.fetchItem(Number(id))
  }

  componentDidUpdate = (prevProps: Props) => {
    const { itemImage } = this.props

    if (itemImage) {
      if (prevProps.itemImage !== itemImage) {
        switch (itemImage.itemImageType) {
          case ItemImageType.Image:
            this.setState({
              itemEditing: {
                ...this.state.itemEditing,
                imageURL: itemImage.url,
                imageFileName: itemImage.fileName,
              },
            })
            break
          case ItemImageType.ThumbnailImage:
            this.setState({
              itemEditing: {
                ...this.state.itemEditing,
                thumbnailImageURL: itemImage.url,
                thumbnailImageFileName: itemImage.fileName,
              },
            })
            break
          default:
        }
      }
    }
  }

  isSavable = () => {
    const {
      beanAmount,
      beanTypeIndex,
      machineButtonIndex,
      name,
      price,
      disposalSeconds,
      description,
      story,
      tastes,
      features,
      color,
    } = this.state.itemEditing

    if (name === '' || description === '' || story === '' || color === '') {
      return false
    } else if (
      price === null ||
      price === 0 ||
      disposalSeconds === null ||
      disposalSeconds === 0 ||
      beanAmount === null ||
      beanAmount === 0 ||
      beanTypeIndex === null ||
      machineButtonIndex === null
    ) {
      return false
    } else if (tastes.length === 0 || features.length === 0) {
      return false
    } else {
      return true
    }
  }

  handleOnClickEdit = (item: Item) => {
    this.setState({ isEditing: true, itemEditing: item })
  }

  handleOnClickQuit = () => {
    this.setState({
      isEditing: false,
      itemEditing: null,
      newFeature: {
        name: '',
        content: '',
        itemFeatureTranslations: [
          { langCode: LANGUAGE_CODES.ENGLISH, name: '', content: '' },
        ],
      },
      newTaste: {
        name: '',
        value: null,
        itemTasteTranslations: [{ langCode: LANGUAGE_CODES.ENGLISH, name: '' }],
      },
      pageActiveLanguage: LANGUAGE_CODES.JAPANESE,
    })
  }

  handleOnClickSave = (id: number) => {
    this.props.setItem(id, this.state.itemEditing)
    this.setState({
      isEditing: false,
      newFeature: {
        name: '',
        content: '',
        itemFeatureTranslations: [
          { langCode: LANGUAGE_CODES.ENGLISH, name: '', content: '' },
        ],
      },
      newTaste: {
        name: '',
        value: null,
        itemTasteTranslations: [{ langCode: LANGUAGE_CODES.ENGLISH, name: '' }],
      },
      pageActiveLanguage: LANGUAGE_CODES.JAPANESE,
    })
  }

  handleOnChangeItemName = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const name = event.target.name
    const value = event.target.value

    if (name === 'name') {
      this.setState({
        itemEditing: {
          ...this.state.itemEditing,
          [name]: value,
        },
      })
    } else if (name === 'translationEnName') {
      const itemTranslations = updateItemTranslation(
        this.state.itemEditing.itemTranslations,
        'en_US',
        'name',
        value
      )
      this.setState({
        itemEditing: {
          ...this.state.itemEditing,
          itemTranslations,
        },
      })
    }
  }

  handleOnChangeManufacturingInfo = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const name = event.target.name
    const value = event.target.value
    if (
      name === 'machineButtonIndex' ||
      name === 'beanTypeIndex' ||
      name === 'beanAmount'
    ) {
      const re = /^[0-9\b]+$/
      if (value === '') {
        this.setState({
          itemEditing: {
            ...this.state.itemEditing,
            [name]: null,
          },
        })
      }
      if (re.test(value)) {
        this.setState({
          itemEditing: {
            ...this.state.itemEditing,
            [name]: Number(value),
          },
        })
      }
    }
  }

  handleOnChangeItemInfo = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const name = event.target.name
    const value = event.target.value
    if (name === 'description' || name === 'story' || name === 'name') {
      this.setState({
        itemEditing: {
          ...this.state.itemEditing,
          [name]: value,
        },
      })
    } else if (name === 'itemTranslations.en_US.name') {
      const itemTranslations = updateItemTranslation(
        this.state.itemEditing.itemTranslations,
        LANGUAGE_CODES.ENGLISH,
        'name',
        value
      )
      this.setState({
        itemEditing: {
          ...this.state.itemEditing,
          itemTranslations,
        },
      })
    } else if (name === 'itemTranslations.en_US.description') {
      const itemTranslations = updateItemTranslation(
        this.state.itemEditing.itemTranslations,
        LANGUAGE_CODES.ENGLISH,
        'description',
        value
      )
      this.setState({
        itemEditing: {
          ...this.state.itemEditing,
          itemTranslations,
        },
      })
    } else if (name === 'itemTranslations.en_US.story') {
      const itemTranslations = updateItemTranslation(
        this.state.itemEditing.itemTranslations,
        LANGUAGE_CODES.ENGLISH,
        'story',
        value
      )
      this.setState({
        itemEditing: {
          ...this.state.itemEditing,
          itemTranslations,
        },
      })
    } else if (
      name === 'cupType' ||
      name === 'price' ||
      name === 'disposalSeconds'
    ) {
      const re = /^[0-9\b]+$/
      if (value === '') {
        this.setState({
          itemEditing: {
            ...this.state.itemEditing,
            [name]: null,
          },
        })
      }
      if (re.test(value)) {
        this.setState({
          itemEditing: {
            ...this.state.itemEditing,
            [name]: Number(value),
          },
        })
      }
    }
  }

  handleOnChangeColor = (color: string) => {
    this.setState({
      itemEditing: {
        ...this.state.itemEditing,
        color,
      },
    })
  }

  handleOnPageLanguageChange = (language: SupportedLanguageCode) => {
    this.setState({ pageActiveLanguage: language })
  }

  // ヘルパーメソッド
  private getElementIndex = (element: HTMLElement): number | null => {
    const indexStr = element.getAttribute('data-num')
    const index = Number(indexStr)
    return isNaN(index) ? null : index
  }

  private extractLanguageCode = (fieldName: string): string | null => {
    const parts = fieldName.split('.')
    return parts.length >= 2 ? parts[1] : null
  }

  private updateDirectField = <T,>(
    item: T,
    field: keyof T,
    value: string
  ): T => ({
    ...item,
    [field]: value,
  })

  private updateNumericField = (taste: Taste, value: string): Taste => {
    if (value === '') {
      return { ...taste, value: null }
    }

    if (EachItemScreen.NUMERIC_PATTERN.test(value)) {
      return { ...taste, value: Number(value) }
    }

    return taste
  }

  private updateTasteTranslation = (
    taste: Taste,
    fieldName: string,
    value: string
  ): Taste => {
    const languageCode = this.extractLanguageCode(fieldName)
    if (!languageCode) {
      return taste
    }

    const updatedTranslations = updateTasteTranslation(
      taste.itemTasteTranslations,
      languageCode,
      value
    )

    return { ...taste, itemTasteTranslations: updatedTranslations }
  }

  private updateFeatureTranslation = (
    feature: Feature,
    fieldName: string,
    value: string
  ): Feature => {
    const parts = fieldName.split('.')
    if (parts.length < 3) {
      return feature
    }

    const languageCode = parts[1]
    const field = parts[2] as 'name' | 'content'

    const updatedTranslations = updateFeatureTranslation(
      feature.itemFeatureTranslations,
      languageCode,
      field,
      value
    )

    return { ...feature, itemFeatureTranslations: updatedTranslations }
  }

  private updateTasteByFieldType = (
    taste: Taste,
    fieldName: string,
    fieldValue: string
  ): Taste => {
    if (fieldName === 'name') {
      return this.updateDirectField(taste, 'name', fieldValue)
    }

    if (fieldName.startsWith(EachItemScreen.TRANSLATION_PREFIX)) {
      return this.updateTasteTranslation(taste, fieldName, fieldValue)
    }

    if (fieldName === 'value') {
      return this.updateNumericField(taste, fieldValue)
    }

    return taste
  }

  private updateFeatureByFieldType = (
    feature: Feature,
    fieldName: string,
    fieldValue: string
  ): Feature => {
    if (fieldName === 'name' || fieldName === 'content') {
      return this.updateDirectField(
        feature,
        fieldName as keyof Feature,
        fieldValue
      )
    }

    if (fieldName.startsWith(EachItemScreen.FEATURE_TRANSLATION_PREFIX)) {
      return this.updateFeatureTranslation(feature, fieldName, fieldValue)
    }

    return feature
  }

  private updateItemEditingTastes = (tastes: Taste[]) => {
    this.setState({
      itemEditing: {
        ...this.state.itemEditing,
        tastes,
      },
    })
  }

  private updateItemEditingFeatures = (features: Feature[]) => {
    this.setState({
      itemEditing: {
        ...this.state.itemEditing,
        features,
      },
    })
  }

  handleOnChangeTastes = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    const tasteIndex = this.getElementIndex(event.currentTarget)

    if (tasteIndex === null) {
      return
    }

    const updatedTastes = this.state.itemEditing.tastes.slice()
    const currentTaste = updatedTastes[tasteIndex]

    updatedTastes[tasteIndex] = this.updateTasteByFieldType(
      currentTaste,
      name,
      value
    )

    this.updateItemEditingTastes(updatedTastes)
  }

  handleOnChangeNewTastes = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    const updatedNewTaste = this.updateTasteByFieldType(
      this.state.newTaste,
      name,
      value
    )

    this.setState({ newTaste: updatedNewTaste })
  }

  handleOnAddNewTaste = () => {
    const tastes = this.state.itemEditing.tastes.slice()
    tastes.push(this.state.newTaste)

    this.setState({
      itemEditing: { ...this.state.itemEditing, tastes },
      newTaste: {
        name: '',
        value: null,
        itemTasteTranslations: [
          { langCode: LANGUAGE_CODES.JAPANESE, name: '' },
          { langCode: LANGUAGE_CODES.ENGLISH, name: '' },
        ],
      },
    })
  }

  handleOnDeleteTaste = (event: React.MouseEvent<HTMLElement>) => {
    const index = Number(event.currentTarget.getAttribute('data-num'))
    const tastes = this.state.itemEditing.tastes.slice()
    tastes.splice(index, 1)
    this.setState({
      itemEditing: { ...this.state.itemEditing, tastes },
    })
  }

  handleOnChangeFeatures = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    const featureIndex = this.getElementIndex(event.currentTarget)

    if (featureIndex === null) {
      return
    }

    const updatedFeatures = this.state.itemEditing.features.slice()
    const currentFeature = updatedFeatures[featureIndex]

    updatedFeatures[featureIndex] = this.updateFeatureByFieldType(
      currentFeature,
      name,
      value
    )

    this.updateItemEditingFeatures(updatedFeatures)
  }

  handleOnChangeNewFeature = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    const updatedNewFeature = this.updateFeatureByFieldType(
      this.state.newFeature,
      name,
      value
    )

    this.setState({ newFeature: updatedNewFeature })
  }

  handleOnAddNewFeature = () => {
    const features = this.state.itemEditing.features.slice()
    features.push(this.state.newFeature)
    this.setState({
      itemEditing: { ...this.state.itemEditing, features },
      newFeature: {
        name: '',
        content: '',
        itemFeatureTranslations: [
          { langCode: LANGUAGE_CODES.JAPANESE, name: '', content: '' },
          { langCode: LANGUAGE_CODES.ENGLISH, name: '', content: '' },
        ],
      },
    })
  }

  handleOnDeleteFeature = (event: React.MouseEvent<HTMLLIElement>) => {
    const index = Number(event.currentTarget.getAttribute('data-num'))
    const features = this.state.itemEditing.features.slice()
    features.splice(index, 1)
    this.setState({
      itemEditing: { ...this.state.itemEditing, features },
    })
  }

  handleOnChangeMatchItemInfo = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const re = /^[0-9\b]+$/
    const name = event.target.name
    const value = event.target.value
    const matchItemParam = { ...this.state.itemEditing.matchItemParam }
    if (
      name === 'bitterness' ||
      name === 'fragrance' ||
      name === 'acidity' ||
      name === 'sweetness' ||
      name === 'richness' ||
      name === 'uniqueness'
    ) {
      if (value === '') {
        matchItemParam[name] = 0
        this.setState({
          itemEditing: {
            ...this.state.itemEditing,
            matchItemParam,
          },
        })
      }
      if (re.test(value)) {
        matchItemParam[name] = Number(value)
        this.setState({
          itemEditing: {
            ...this.state.itemEditing,
            matchItemParam,
          },
        })
      }
    }
  }

  render() {
    const id = this.props.match.params.id
    const item = this.props.currentItem
    const {
      isEditing,
      itemEditing,
      newTaste,
      newFeature,
      pageActiveLanguage,
    } = this.state
    const { itemImage, uploadItemImage } = this.props
    return (
      <Layout location={'items'}>
        <Wrapper title={`ID: ${id} ${item ? item.name : ''}`}>
          {item && (
            <>
              {isEditing ? (
                <>
                  <Button
                    className={styles.itemEditButton}
                    title={strings.quit}
                    onClick={() => this.handleOnClickQuit()}
                  />
                  <Button
                    className={styles.itemEditButton}
                    title={strings.save}
                    disabled={!this.isSavable()}
                    onClick={() => this.handleOnClickSave(id)}
                  />
                  <PageLanguageTabs
                    activeLanguage={pageActiveLanguage}
                    onLanguageChange={this.handleOnPageLanguageChange}
                  />
                  <h3 className={styles.title}>{strings.manufacturingInfo}</h3>
                  <ManufacturingInfoForm
                    item={itemEditing}
                    activeLanguage={pageActiveLanguage}
                    onChange={event =>
                      this.handleOnChangeManufacturingInfo(event)
                    }
                  />
                  <h3 className={styles.title}>{strings.itemInfo}</h3>
                  <ItemInfoForm
                    item={itemEditing}
                    activeLanguage={pageActiveLanguage}
                    onChange={event => this.handleOnChangeItemInfo(event)}
                    onChangeColor={color => this.handleOnChangeColor(color)}
                  />
                  <h3 className={styles.title}>{strings.tastes}</h3>
                  <TastesForm
                    item={itemEditing}
                    newTaste={newTaste}
                    activeLanguage={pageActiveLanguage}
                    onChangeTastes={event => this.handleOnChangeTastes(event)}
                    onChangeNewTaste={event =>
                      this.handleOnChangeNewTastes(event)
                    }
                    onAddNewTaste={() => this.handleOnAddNewTaste()}
                    onDeleteTaste={this.handleOnDeleteTaste}
                  />
                  <h3 className={styles.title}>{strings.features}</h3>
                  <FeaturesForm
                    features={itemEditing.features || []}
                    newFeature={newFeature}
                    activeLanguage={pageActiveLanguage}
                    onChangeFeatures={event =>
                      this.handleOnChangeFeatures(event)
                    }
                    onChangeNewFeature={event =>
                      this.handleOnChangeNewFeature(event)
                    }
                    onAddNewFeature={() => this.handleOnAddNewFeature()}
                    onDeleteFeature={this.handleOnDeleteFeature}
                  />
                  <h3 className={styles.title}>{strings.matchItem}</h3>
                  <MatchItemInfoForm
                    item={itemEditing}
                    activeLanguage={pageActiveLanguage}
                    onChange={event => this.handleOnChangeMatchItemInfo(event)}
                  />
                  <ItemImageForms
                    id={id}
                    item={item}
                    itemImage={itemImage}
                    uploadItemImage={uploadItemImage}
                  />
                </>
              ) : (
                <>
                  <Button
                    className={styles.itemEditButton}
                    title={strings.edit}
                    onClick={() => this.handleOnClickEdit(item)}
                  />
                  <PageLanguageTabs
                    activeLanguage={pageActiveLanguage}
                    onLanguageChange={this.handleOnPageLanguageChange}
                  />
                  <h3 className={styles.title}>{strings.manufacturingInfo}</h3>
                  <ManufacturingInfoTable item={item} />
                  <h3 className={styles.title}>{strings.itemInfo}</h3>
                  <ItemInfoTable
                    item={item}
                    activeLanguage={pageActiveLanguage}
                  />
                  <h3 className={styles.title}>{strings.tastes}</h3>
                  <TastesTable
                    item={item}
                    activeLanguage={pageActiveLanguage}
                  />
                  <h3 className={styles.title}>{strings.features}</h3>
                  <FeaturesTable
                    item={item}
                    activeLanguage={pageActiveLanguage}
                  />
                  <h3 className={styles.title}>{strings.matchItem}</h3>
                  <MatchItemInfoTable item={item} />
                  <h3 className={styles.title}>{strings.images}</h3>
                  <ItemImageItems item={item} />
                </>
              )}
            </>
          )}
        </Wrapper>
      </Layout>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  currentItem: state.items.currentItem,
  itemImage: state.items.itemImage,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchItem: (id: number) => dispatch(itemsActions.fetchItem(id)),
  setItem: (id: number, item: Item) => dispatch(itemsActions.setItem(id, item)),
  uploadItemImage: (id: number, itemImageType: ItemImageType, file: File) =>
    dispatch(itemsActions.uploadItemImage(id, itemImageType, file)),
})

const ConnectedEachItemScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(EachItemScreen)

export default withPermissionCheck(ConnectedEachItemScreen, {
  requiredPermission: Permission.ITEM_READ,
  redirectTo: '/404',
})
